import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { useTheme } from '../contexts/ThemeContext';

export function useCapacitor() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    // Nur auf nativen Plattformen ausführen
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    initializeApp();
    initializePushNotifications();

    // Cold Start: App wurde per Deep Link neu gestartet
    CapacitorApp.getLaunchUrl().then((result) => {
      if (result?.url) {
        console.log('[DeepLink] 🚀 Cold start URL:', result.url);
        try {
          const url = new URL(result.url);
          const path = url.pathname + url.search;
          navigate(path, { replace: true });
        } catch (error) {
          console.error('[DeepLink] ❌ Error parsing cold start URL:', error);
        }
      }
    });

    // Back Button Handler (appStateChange wird vom SocketContext verwaltet)
    const backButtonListener = CapacitorApp.addListener('backButton', ({ canGoBack }: { canGoBack: boolean }) => {
      if (!canGoBack) {
        CapacitorApp.exitApp();
      } else {
        window.history.back();
      }
    });

    // Deep Link / App Link Handler (App läuft bereits im Hintergrund)
    const appUrlListener = CapacitorApp.addListener('appUrlOpen', (event: any) => {
      console.log('[DeepLink] ✅ App opened with URL:', event.url);
      
      // URL parsen: https://meinelisten.app/invite/abc123 -> /invite/abc123
      try {
        const url = new URL(event.url);
        const path = url.pathname + url.search;
        
        console.log('[DeepLink] ✅ Navigating to:', path);
        navigate(path);
      } catch (error) {
        console.error('[DeepLink] ❌ Error parsing URL:', error);
      }
    });

    return () => {
      // Listener entfernen
      backButtonListener.then(listener => listener.remove());
      appUrlListener.then(listener => listener.remove());
    };
  }, []);

  // Status Bar Farbe bei Theme-Wechsel anpassen
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    StatusBar.setStyle({
      style: isDarkMode ? Style.Dark : Style.Light,
    }).catch((err: any) => console.error('[Capacitor] StatusBar style error:', err));
    
    StatusBar.setBackgroundColor({
      color: isDarkMode ? '#0d1117' : '#fdfbf7',
    }).catch((err: any) => console.error('[Capacitor] StatusBar background error:', err));
  }, [isDarkMode]);

  const initializeApp = async () => {
    try {
      // StatusBar anzeigen und konfigurieren
      await StatusBar.show();
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#fdfbf7' });

      // Splash Screen verstecken
      await SplashScreen.hide();

      console.log('[Capacitor] App initialized - StatusBar visible');
    } catch (error) {
      console.error('[Capacitor] Initialization error:', error);
    }
  };

  const initializePushNotifications = async () => {
    try {
      // WICHTIG: Listener ZUERST setzen, bevor register() aufgerufen wird!
      // Sonst Race Condition: Token kommt vor Listener-Setup
      
      // Listener für Token Registration
      PushNotifications.addListener('registration', (token: any) => {
        console.log('🎉🎉🎉 TOKEN GEFUNDEN:', token.value);
        console.log('[Push] Registration token:', token.value);
        // Token an Backend senden
        sendTokenToBackend(token.value);
      });

      // Listener für Registration Errors
      PushNotifications.addListener('registrationError', (error: any) => {
        console.error('❌❌❌ FEHLER BEI REGISTRIERUNG:', error);
        console.error('[Push] Registration error:', error);
      });

      // Permission Check
      console.log('[Push] Checking permissions...');
      let permStatus = await PushNotifications.checkPermissions();
      console.log('[Push] Permission status:', permStatus);

      if (permStatus.receive === 'prompt') {
        console.log('[Push] Requesting permissions...');
        permStatus = await PushNotifications.requestPermissions();
        console.log('[Push] Permission granted:', permStatus.receive === 'granted');
      }

      if (permStatus.receive !== 'granted') {
        console.log('[Push] Permission not granted');
        return;
      }

      // JETZT ERST registrieren (nachdem Listener gesetzt sind)
      console.log('[Push] Registering with FCM...');
      await PushNotifications.register();

      // Listener für empfangene Push Notifications
      PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
        console.log('🔔🔔🔔 PUSH NOTIFICATION EMPFANGEN!!!', notification);
        console.log('[Push] Notification received:', notification);
        console.log('[Push] Title:', notification.title);
        console.log('[Push] Body:', notification.body);
        // Notification wird automatisch angezeigt
      });

      // Listener für Notification Actions
      PushNotifications.addListener('pushNotificationActionPerformed', (notification: any) => {
        console.log('[Push] Notification action performed:', notification);
        
        // Navigation basierend auf Notification Data
        const data = notification.notification.data;
        if (data.listId) {
          window.location.href = `/lists/${data.listId}`;
        }
      });

      console.log('[Push] Notifications initialized');
    } catch (error: any) {
      // Firebase nicht konfiguriert? Kein Problem, App läuft trotzdem
      if (error?.message?.includes('FirebaseApp')) {
        console.warn('[Push] Firebase not configured yet. Push notifications disabled.');
        console.warn('[Push] To enable: Add google-services.json to android/app/ folder');
      } else {
        console.error('[Push] Error:', error);
      }
      // App läuft normal weiter, auch ohne Push-Benachrichtigungen
    }
  };

  const sendTokenToBackend = async (token: string) => {
    try {
      // Prüfen ob User eingeloggt ist
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        console.log('[Push] User not logged in, storing token locally');
        localStorage.setItem('pendingPushToken', token);
        console.log('[Push] Token saved to localStorage:', localStorage.getItem('pendingPushToken') ? 'SUCCESS' : 'FAILED');
        return;
      }

      const api = (await import('../services/api')).default;
      console.log('[Push] Sending token to backend:', token.substring(0, 50) + '...');
      await api.post('/auth/push-token', { 
        token,
        platform: 'android',
        deviceInfo: navigator.userAgent
      });
      console.log('[Push] Token registered with backend');
      // Token erfolgreich gesendet, pending token löschen und als aktuellen Token speichern
      localStorage.removeItem('pendingPushToken');
      localStorage.setItem('currentPushToken', token);
    } catch (error) {
      console.error('[Push] Error sending token:', error);
      // Bei Fehler Token für späteren Versuch speichern
      localStorage.setItem('pendingPushToken', token);
    }
  };
}
